import React, { useEffect, useState } from "react";
import { Tabs, Tab } from "@mui/material";
import { styled } from "@mui/material/styles";

// Styling for the tabs container (same as RemovalTab)
const GradientTabs = styled(Tabs)({
  overflow: "hidden",
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

interface GradientTabProps {
  color: string;
  isFirstTab: boolean;
  isLastTab?: boolean;
  tabStatus: "before" | "selected" | "after";
  disabled: boolean;
}

const GradientTab = styled(Tab)<GradientTabProps>(
  ({ color, isFirstTab, tabStatus, disabled, isLastTab }) => ({
    textTransform: "none",
    color: disabled
      ? "#7f7f7f"
      : tabStatus === "after"
      ? "#1F1F1F"
      : isLastTab
      ? "#7f7f7f"
      : "#ffffff",
    fontWeight: 600,
    fontSize: "13px",
    width: "108px",
    padding: "0px 24px !important",
    paddingLeft: isFirstTab ? "none" : "40px !important",
    textAlign: "center",
    backgroundColor: disabled
      ? "#e9e9e9"
      : tabStatus === "selected"
      ? "blue"
      : tabStatus === "before"
      ? color
      : "#e9e9e9",
    opacity: 1,
    clipPath: isFirstTab
      ? "polygon(0% 0%, 75% 0%, 100% 50%, 75% 100%, 0% 100%)"
      : "polygon(75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%, 0% 0%)",
    transition: "transform 0.3s ease-in-out",
    marginLeft: isFirstTab ? "0px" : "-20px",
    "&.Mui-selected": {
      color: disabled ? "#7f7f7f" : "#ffffff",
      fontWeight: "bold",
      opacity: 1,
      backgroundColor: disabled ? "#e9e9e9" : color,
      border: "0px solid",
    },
    "&.Mui-selected:last-child": {
      color: "#444444",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "rgba(100, 95, 228, 0.32)",
    },
  })
);

interface StorageTabProps {
  leadDetails: any;
}

const StorageTab: React.FC<StorageTabProps> = ({ leadDetails }) => {
  const status = leadDetails?.stage;
  const normalizedStatus = status?.toUpperCase();
  const hasStatus = !!status;

  // Storage-specific status → tab index mapping
  const statusToIndex: { [key: string]: number } = React.useMemo(
    () => ({
      STORAGE_ENQUIRY: 0,
      STORAGE: 1,
      COMPLETE: 2,
    }),
    []
  );

  const [value, setValue] = useState(statusToIndex[normalizedStatus] ?? 0);

  useEffect(() => {
    setValue(statusToIndex[normalizedStatus] ?? 0);
  }, [normalizedStatus, statusToIndex]);

  // Three storage tabs
  const tabLabels = ["Storage Enquiry", "Storage", "Complete"];

  // Colors matching the screenshot
  const tabColors = [
    "#FF66C4", // Storage Enquiry – pink
    "#D16B2C", // Storage – brown/orange
    "#7ED957", // Complete – green
  ];

  return (
    <GradientTabs
      sx={{ padding: "6px 0px 6px 8px", borderTop: "1px solid #C4CDD5" }}
      value={value}
      aria-label="storage status tabs"
    >
      {tabColors.map((color, index) => (
        <GradientTab
          key={index}
          label={tabLabels[index]}
          color={hasStatus ? color : "#7f7f7f"}
          isFirstTab={index === 0}
          isLastTab={index === tabColors.length - 1}
          tabStatus={
            index < value ? "before" : index === value ? "selected" : "after"
          }
          disabled={!hasStatus}
        />
      ))}
    </GradientTabs>
  );
};

export default StorageTab;
